import {
  Lock,
  KeyRound,
  Network,
  ScrollText,
  Activity,
  BadgeCheck,
  Rocket,
  Landmark,
  Share2,
  Building2,
  Plus,
} from "lucide-react";
import { Container, SectionHead, FeatureGrid } from "@/components/common/Section";
import { Reveal, useInView, useCountUp } from "@/lib/anim";
import { SOLUTION_GROUPS, SECURITY_CAPABILITIES } from "@/lib/content";
import ConnectorsNetwork from "@/components/visuals/ConnectorsNetwork";

/* ---------- Section 15: Security ---------- */

const SECURITY_ICONS = [
  <Lock className="h-5 w-5" key="1" />,
  <KeyRound className="h-5 w-5" key="2" />,
  <Network className="h-5 w-5" key="3" />,
  <ScrollText className="h-5 w-5" key="4" />,
  <Activity className="h-5 w-5" key="5" />,
  <BadgeCheck className="h-5 w-5" key="6" />,
];

export function SecuritySection() {
  return (
    <section data-testid="security-section" className="relative overflow-hidden bg-[#1D305B] py-20 lg:py-28">
      <div className="bg-grid-dark absolute inset-0 opacity-50 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_20%,black,transparent)]" aria-hidden="true" />
      <Container className="relative">
        <SectionHead
          dark
          eyebrow="Security by Design"
          title={<>Security is <span className="text-brand-gradient-light">engineered into the platform.</span></>}
          copy="Payment infrastructure demands more than functionality. It requires security, control and operational resilience at every layer. iJeeva designs payment platforms with security, encryption, access control, monitoring and auditability at their core."
        />
        <Reveal className="mt-10">
          <div data-testid="pci-badge" className="inline-flex items-center gap-4 rounded-full border border-[#5EEAD4]/30 bg-[#5EEAD4]/10 py-2.5 pl-4 pr-6">
            <img src="/pci-ssc-white.png" alt="PCI Security Standards Council" className="h-7 w-auto" />
            <p className="text-sm font-medium text-[#DFF7F3]">ZenPay platform is a PCI-certified secure platform</p>
          </div>
        </Reveal>
        <div className="mt-14">
          <FeatureGrid
            dark
            testidPrefix="security-capability"
            items={SECURITY_CAPABILITIES.map((c, i) => ({ ...c, icon: SECURITY_ICONS[i] }))}
          />
        </div>
      </Container>
    </section>
  );
}

/* ---------- Section 16: Solutions (editorial) ---------- */

export function SolutionsSection() {
  return (
    <section data-testid="solutions-section" className="py-20 lg:py-28">
      <Container>
        <SectionHead
          eyebrow="What We Do"
          title="From payment strategy to production."
          copy="Capabilities organized around financial technology, not a generic services catalogue."
        />
        <div className="mt-14">
          {SOLUTION_GROUPS.map((g, gi) => (
            <Reveal
              key={g.num}
              delay={gi * 60}
              className={`grid gap-6 border-t border-[#E2E8F0] py-10 md:grid-cols-12 ${gi === SOLUTION_GROUPS.length - 1 ? "border-b" : ""}`}
            >
              <div className="md:col-span-4">
                <h3 className="text-xl font-semibold text-[#1D305B] sm:text-2xl">{g.title}</h3>
              </div>
              <ul className="grid gap-x-10 gap-y-3 sm:grid-cols-2 md:col-span-8">
                {g.items.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-[15px] text-[#475569]">
                    <Plus className="h-3.5 w-3.5 shrink-0 text-[#0EA5A4]" strokeWidth={2.5} aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ---------- Section 17: Connected Finance ---------- */

function ConnectorStat() {
  const [ref, inView] = useInView();
  const count = useCountUp(60, inView);
  return (
    <div ref={ref} className="mt-10 flex items-end gap-4">
      <p className="text-[64px] font-bold leading-none tracking-tight text-[#1D305B]">
        {count}
        <span className="text-[#2563EB]">+</span>
      </p>
      <p className="pb-2 text-sm font-medium leading-snug text-[#64748B]">
        pre-built
        <br />
        connectors
      </p>
    </div>
  );
}

export function ConnectedFinanceSection() {
  return (
    <section data-testid="connected-finance-section" className="border-y border-[#E2E8F0] bg-white py-20 lg:py-28">
      <Container className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <span className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#2563EB]">
            <span className="h-px w-6 bg-[#2563EB]/50" aria-hidden="true" />
            Connected Finance
          </span>
          <h2 className="mt-4 text-[32px] font-semibold leading-[1.12] tracking-tight text-[#1D305B] sm:text-4xl lg:text-[44px]">
            Connect once. <span className="text-brand-gradient">Build beyond.</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#64748B] sm:text-lg">
            Financial products rarely operate in isolation. iJeeva platforms connect banks, payment providers, KYC
            services, fraud systems, FX providers, card networks and other financial infrastructure through an
            extensible integration architecture.
          </p>
          <ConnectorStat />
        </Reveal>
        <Reveal delay={150}>
          <ConnectorsNetwork />
        </Reveal>
      </Container>
    </section>
  );
}

/* ---------- Section 18: Customer Types ---------- */

const CUSTOMER_TYPES = [
  {
    icon: <Rocket className="h-5 w-5" />,
    title: "Fintechs",
    desc: "Launch financial products faster with configurable payment infrastructure.",
  },
  {
    icon: <Landmark className="h-5 w-5" />,
    title: "Banks & Financial Institutions",
    desc: "Modernize payment and banking capabilities while integrating with existing infrastructure.",
  },
  {
    icon: <Share2 className="h-5 w-5" />,
    title: "Payment Service Providers",
    desc: "Operate multi-tenant payment environments with flexible routing and integrations.",
  },
  {
    icon: <Building2 className="h-5 w-5" />,
    title: "Enterprises",
    desc: "Connect payment capabilities to existing business and financial workflows.",
  },
];

export function CustomerTypesSection() {
  return (
    <section data-testid="customer-types-section" className="py-20 lg:py-28">
      <Container>
        <SectionHead eyebrow="Built For" title="Built for businesses moving money." />
        <div className="mt-14">
          <FeatureGrid items={CUSTOMER_TYPES} cols={4} testidPrefix="customer-type" />
        </div>
      </Container>
    </section>
  );
}
